import Cookies from "js-cookie"
import { create } from "zustand"

import { AUTH_COOKIE_NAME } from "@/constants/auth"
import type { Filters } from "@/constants/filters"
import type { FlatModalDataMap, SetModalData } from "@/constants/modals"
import type { StudentProfile } from "@/types/profileTypes"

const userData = JSON.parse(Cookies.get(AUTH_COOKIE_NAME) || "null")

interface Store {
  sideBarOpen: boolean
  setSideBarOpen: () => void

  authData?: { token: string; user: StudentProfile }
  setAuthData: (data: Store["authData"]) => void

  filters: Record<Filters, Record<string, unknown>>
  editFilters: (filterName: Filters, value: unknown) => void

  modalsStates: FlatModalDataMap
  editModalState: SetModalData

  theme: {
    primaryColor: string
    secondaryColor: string
  }
  setPrimaryColor: (color: string) => void
  setSecondaryColor: (color: string) => void

  branding: {
    siteLogo: string
    favicon: string
    loginLogo: string
    siteTitle: string
  }
  setBrandingAssets: (assets: Store["branding"]) => void

  adBannerDismissed: boolean
  setAdBannerDismissed: (dismissed: boolean) => void

  adModalShown: boolean
  setAdModalShown: (shown: boolean) => void

  absenceAlertShow: boolean
  setAbsenceAlertShow: (show: boolean) => void

  missedTasksAlertShow: boolean
  setMissedTasksAlertShow: (show: boolean) => void
}

export const useStore = create<Store>((set) => ({
  sideBarOpen: window.innerWidth > 600 ? false : true,
  setSideBarOpen: () => {
    set((state) => ({
      sideBarOpen: !state.sideBarOpen,
    }))
  },

  authData: !userData ? "" : userData,
  setAuthData: (data) => {
    set(() => ({ authData: data }))
    Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(data))
  },

  filters: {} as Store["filters"],
  editFilters: (filterName, value) => {
    set((store) => ({
      filters: { ...store.filters, [filterName]: value },
    }))
  },

  modalsStates: {} as Store["modalsStates"],
  editModalState: (modal, state) =>
    set((store) => ({
      modalsStates: { ...store.modalsStates, [modal]: state },
    })),

  theme: {
    primaryColor: "#243b78",
    secondaryColor: "#e74c3c",
  },
  setPrimaryColor: (color) => {
    set((state) => ({
      theme: {
        ...state.theme,
        primaryColor: color,
      },
    }))
    document.documentElement.style.setProperty("--primary", color)
  },
  setSecondaryColor: (color) => {
    set((state) => ({
      theme: {
        ...state.theme,
        secondaryColor: color,
      },
    }))
    document.documentElement.style.setProperty("--secondry", color)
  },

  branding: {
    siteLogo: "",
    favicon: "",
    loginLogo: "",
    siteTitle: "", // <-- New
  },

  setBrandingAssets: (assets) => {
    set((state) => ({
      branding: {
        ...state.branding,
        ...assets,
      },
    }))

    // Optional: Dynamically update favicon in <head>
    if (assets.favicon) {
      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link")
      link.type = "image/x-icon"
      link.rel = "shortcut icon"
      link.href = assets.favicon
      document.head.appendChild(link)
    }

    // ✅ Optional: Update document title
    if (assets.siteTitle) {
      document.title = assets.siteTitle
    }
  },

  // Advertisement state
  adBannerDismissed: false,
  adModalShown: false,
  setAdBannerDismissed: (dismissed) => {
    set(() => ({ adBannerDismissed: dismissed }))
  },
  setAdModalShown: (shown) => {
    set(() => ({ adModalShown: shown }))
  },

  absenceAlertShow: true,
  setAbsenceAlertShow: (show: boolean) => {
    set(() => ({ absenceAlertShow: show }))
  },

  missedTasksAlertShow: true,
  setMissedTasksAlertShow: (show: boolean) => {
    set(() => ({ missedTasksAlertShow: show }))
  },
}))
